import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Typography, Alert, CircularProgress, Chip, List, ListItem, ListItemIcon, ListItemText, Checkbox, Stack, Tabs, Tab, Tooltip, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Card, CardContent, Paper, Collapse, Divider, Skeleton
} from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyRounded from '@mui/icons-material/KeyRounded';
import DataObjectRounded from '@mui/icons-material/DataObjectRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import SecurityRounded from '@mui/icons-material/SecurityRounded';
import SettingsEthernetRounded from '@mui/icons-material/SettingsEthernetRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { SnackbarContext } from '../ui/SnackbarProvider';
// Alignments only; no Grid needed here
import { buildApiUrl, config } from '../config';
import { ReactComponent as GoogleDriveSvg } from '../assets/connectors/google-drive.svg';
import { ReactComponent as PostgresSvg } from '../assets/connectors/postgres.svg';
import { ReactComponent as MySQLSvg } from '../assets/connectors/mysql.svg';
import { ReactComponent as OneDriveSvg } from '../assets/connectors/onedrive.svg';
import { ReactComponent as SharePointSvg } from '../assets/connectors/sharepoint.svg';
import { ReactComponent as GoogleAdsSvg } from '../assets/connectors/google-ads.svg';
import { ReactComponent as BigQuerySvg } from '../assets/connectors/bigquery.svg';
import { ReactComponent as SnowflakeSvg } from '../assets/connectors/Snowflake.svg';
import { ReactComponent as RedshiftSvg } from '../assets/connectors/redshift.svg';
import { ReactComponent as ShopifySvg } from '../assets/connectors/shopify.svg';
import { ReactComponent as HubSpotSvg } from '../assets/connectors/hubspot.svg';
import { ReactComponent as MCPSvg } from '../assets/connectors/mcp.svg';
import { ReactComponent as AirtableSvg } from '../assets/connectors/airtable.svg';
import GoogleDrivePicker, { GoogleDriveFile } from '../components/GoogleDrivePicker';
import { GOOGLE_DRIVE_MIME_TYPES } from '../utils/googlePicker';
import { formatRelativeTime } from '../utils/date';
import { ProfilingTab } from '../components/ProfilingTab';
import ConnectorDetailHeader from './connectorDetail/ConnectorDetailHeader';
import { SyncSparklinePoint } from './connectorDetail/SyncSparkline';
import DriveFilesView from './connectorDetail/DriveFilesView';
import DataSyncSection from './connectorDetail/DataSyncSection';
import DriveFilesPickerModal, { DriveFile } from '../components/DriveFilesPickerModal';

// CopyableLine — read-mode text value with a copy-on-hover affordance.
// Used inside the "Connection Details" rows so the operator can grab a
// hostname, port, schema, etc. without selecting and right-clicking. The
// copy button hides until the row (or this element) is hovered.
function CopyableLine({
  value,
  mono = true,
  breakAll = false,
}: {
  value: any;
  mono?: boolean;
  breakAll?: boolean;
}) {
  const [copied, setCopied] = React.useState(false);
  const text = value == null ? '' : String(value);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1100);
    } catch {
      // Clipboard unavailable — silent no-op
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        '& .copy-btn': { opacity: 0, transition: 'opacity 120ms ease' },
        '&:hover .copy-btn': { opacity: 1 },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          fontFamily: mono ? 'monospace' : 'inherit',
          fontSize: '0.75rem',
          ...(breakAll ? { wordBreak: 'break-all' } : {}),
          flex: 1,
          minWidth: 0,
        }}
      >
        {text || '—'}
      </Typography>
      {!!text && (
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <IconButton
            size="small"
            onClick={onCopy}
            className="copy-btn"
            sx={{ p: 0.25 }}
            aria-label="Copy value"
          >
            {copied ? (
              <CheckCircleRounded sx={{ fontSize: 14, color: 'success.main' }} />
            ) : (
              <ContentCopyRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// Icon mapping for connector types
const connectorIcons: { [key: string]: any } = {
  'postgres': PostgresSvg,
  'mysql': MySQLSvg,
  'google_drive': GoogleDriveSvg,
  'snowflake': SnowflakeSvg,
  'redshift': RedshiftSvg,
  'onedrive': OneDriveSvg,
  'sharepoint': SharePointSvg,
  'google_ads': GoogleAdsSvg,
  'bigquery': BigQuerySvg,
  'shopify': ShopifySvg,
  'hubspot': HubSpotSvg,
  'mcp': MCPSvg,
  'airtable': AirtableSvg,
};

const HUBSPOT_SUPPORTED_RESOURCES = [
  'Contacts',
  'Companies',
  'Deals',
  'Products',
  'Line Items',
  'Quotes',
];

function redactConfig(config: any) {
  if (!config) return {};
  const redacted = { ...config };
  if (redacted.password) redacted.password = '••••••';
  if (redacted.client_secret) redacted.client_secret = '••••••';
  if (redacted.oauth_access_token) redacted.oauth_access_token = '••••••';
  if (redacted.oauth_refresh_token) redacted.oauth_refresh_token = '••••••';
  if (redacted.access_token) redacted.access_token = '••••••';
  if (redacted.private_key) redacted.private_key = '••••••';
  if (redacted.private_key_passphrase) redacted.private_key_passphrase = '••••••';
  if (redacted.service_account_json) redacted.service_account_json = '••••••';
  
  // Redact SSH tunnel credentials
  if (redacted.ssh_tunnel) {
    const sshRedacted = { ...redacted.ssh_tunnel };
    if (sshRedacted.ssh_password) sshRedacted.ssh_password = '••••••';
    if (sshRedacted.ssh_private_key) sshRedacted.ssh_private_key = '••••••';
    if (sshRedacted.ssh_private_key_passphrase) sshRedacted.ssh_private_key_passphrase = '••••••';
    redacted.ssh_tunnel = sshRedacted;
  }
  
  return redacted;
}


// Helper function to get appropriate selection labels based on connector category
function getSelectionLabels(category?: string) {
  switch (category) {
    case 'sql':
      return {
        tabLabel: 'View Tables',
        buttonLabel: 'Tables',
        itemLabel: 'tables',
        itemLabelCapitalized: 'Tables'
      };
    case 'rest':
      return {
        tabLabel: 'View Resources',
        buttonLabel: 'Resources',
        itemLabel: 'resources',
        itemLabelCapitalized: 'Resources'
      };
    case 'files':
      return {
        tabLabel: 'View Files',
        buttonLabel: 'Files',
        itemLabel: 'files',
        itemLabelCapitalized: 'Files'
      };
    default:
      return {
        tabLabel: 'View Items',
        buttonLabel: 'Items',
        itemLabel: 'items',
        itemLabelCapitalized: 'Items'
      };
  }
}

export default function ConnectorDetailPage() {
  const { connectorId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetupMode = searchParams.get('setup') === 'true';
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  // File/table picker modal states
  // 'drive' for Google Drive, or the connector type string (e.g. 'postgres', 'redshift', 'snowflake', etc.)
  const [selectModalOpen, setSelectModalOpen] = useState<false | string>(false);
  const [driveFiles, setDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [savingSelection, setSavingSelection] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedDriveRows, setSelectedDriveRows] = useState<GoogleDriveFile[]>([]);
  const [selectedTableRows, setSelectedTableRows] = useState<string[]>([]);
  const [resourceTypeMap, setResourceTypeMap] = useState<Record<string, string>>({});
  const [allResources, setAllResources] = useState<any[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [fetchingSchema, setFetchingSchema] = useState(false);
  const [schemaMessage, setSchemaMessage] = useState<string | null>(null);
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean | number>(2);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());
  const { notify } = React.useContext(SnackbarContext);
  
  // Setup flow states
  const [connectorName, setConnectorName] = useState('');
  const [namingStep, setNamingStep] = useState(false);
  const [autoFileSelection, setAutoFileSelection] = useState(false);
  const [autoSchemaFetch, setAutoSchemaFetch] = useState(false);

  // Drive files picker modal — opens the canonical DriveFilesPickerModal
  // (same one used on /data-sync) so the user can edit files + per-file
  // sheet selection without leaving this page.
  const [driveModalOpen, setDriveModalOpen] = useState(false);

  // Delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [editedConfig, setEditedConfig] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Header-level: test connection / re-authenticate / sync sparkline.
  // These are kept on the page (rather than the header component) because
  // they share `data` and the existing fetch pipeline.
  const [testing, setTesting] = useState(false);
  const [reauthenticating, setReauthenticating] = useState(false);
  const [lastTestedAt, setLastTestedAt] = useState<number | null>(null);
  // Result of the most recent Test Connection probe. The endpoint is
  // intentionally read-only on the server, so we keep this state on the
  // client purely to surface "verified just now" / "last test failed"
  // copy in the header without mutating the persisted connector.status.
  const [lastTestResult, setLastTestResult] = useState<
    { success: boolean; errorMessage: string | null } | null
  >(null);
  const [syncPoints, setSyncPoints] = useState<SyncSparklinePoint[]>([]);

  // Define which fields are editable (user-entered) vs non-editable (OAuth/system-generated)
  const EDITABLE_FIELDS = ['host', 'port', 'database', 'user', 'password', 'account', 'schema', 'warehouse', 'role', 'shop_url', 'project', 'service_account_json'];
  const NON_EDITABLE_FIELDS = ['oauth_access_token', 'oauth_refresh_token', 'token_expiry', 'access_token', 'client_id', 'client_secret', 'scope'];

  useEffect(() => {
    const tStr = window.localStorage.getItem('klaris_tenant');
    if (!tStr) {
      navigate('/login', { replace: true });
      return;
    }
    const t = JSON.parse(tStr);
    setTenantId(t.tenant_id);
  }, [navigate]);

  useEffect(() => {
    if (tenantId && connectorId) fetchDetail();
    // eslint-disable-next-line
  }, [tenantId, connectorId]);

  // Fetch recent sync jobs (last 20) once on mount so the header sparkline
  // can render immediately. Filtered to jobs that touched this connector.
  // The Activity tab also calls this endpoint and pushes its results
  // back via `onJobsLoaded` to keep both views in sync after refreshes.
  useEffect(() => {
    if (!tenantId || !connectorId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs?limit=20`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) return;
        const result: any[] = await resp.json();
        if (cancelled) return;
        const points: SyncSparklinePoint[] = (result || [])
          .filter((j) => Array.isArray(j.connector_ids) && j.connector_ids.includes(connectorId))
          .map((j) => ({
            jobId: String(j.job_id),
            status: j.status,
            startedAt: j.completed_at || j.updated_at || j.created_at,
          }));
        setSyncPoints(points);
      } catch {
        /* sparkline is best-effort; silent failure is fine */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, connectorId]);

  // Handle reauth callback
  useEffect(() => {
    const reauthSuccess = searchParams.get('reauth');
    if (reauthSuccess === 'success') {
      notify('Connector re-authorized successfully!', 'success');
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Refresh connector data
      if (tenantId && connectorId) {
        fetchDetail();
      }
    }
    // eslint-disable-next-line
  }, [searchParams, tenantId, connectorId]);

  // Setup flow: automatically start naming step for new Google Drive connectors
  useEffect(() => {
    if (isSetupMode && data && data.category === 'files' && !data.name) {
      setNamingStep(true);
    }
  }, [isSetupMode, data]);

  // Load all resources from the live /resources endpoint when on the Tables tab
  useEffect(() => {
    if (tab !== 1) return;
    if (!data || !tenantId || !connectorId) return;
    if (data.category !== 'sql' && data.category !== 'rest') return;
    if (allResources.length > 0) return; // already loaded
    const fetchResources = async () => {
      setResourcesLoading(true);
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/resources`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;
        const result: any[] = await resp.json();
        setAllResources(result || []);
        // Also build type map for use elsewhere
        const typeMap: Record<string, string> = {};
        for (const item of (result || [])) {
          if (item.type) {
            typeMap[item.id || item.name] = item.type;
            if (item.name) typeMap[item.name] = item.type;
          }
        }
        if (Object.keys(typeMap).length > 0) setResourceTypeMap(typeMap);
      } catch {
      } finally {
        setResourcesLoading(false);
      }
    };
    fetchResources();
    // eslint-disable-next-line
  }, [tab, data?.category, tenantId, connectorId]);

  // Auto-load full schema when switching to the combined Tables tab (for column details)
  useEffect(() => {
    const loadSchemaIfNeeded = async () => {
      if (tab !== 1) return;
      if (!tenantId || !connectorId) return;
      const schemaId = data?.schema?.schema_id;
      // If we already have raw_schema in state or in data, no need to fetch
      if (!schemaId || schema?.raw_schema || data?.schema?.raw_schema) return;
      try {
        setSchemaLoading(true);
        setSchemaError(null);
        const token = window.localStorage.getItem('klaris_jwt');
        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/schemas/${schemaId}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) {
          const result = await resp.json();
          throw new Error(result?.detail || 'Failed to fetch schema');
        }
        const result = await resp.json();
        setSchema(result);
      } catch (err: any) {
        setSchemaError(err.message || 'Failed to fetch schema');
      } finally {
        setSchemaLoading(false);
      }
    };
    loadSchemaIfNeeded();
    // eslint-disable-next-line
  }, [tab, tenantId, connectorId, data?.schema?.schema_id]);

  const fetchDetail = async () => {
    if (!tenantId || !connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      // Fetch all connectors, find ours (can optimize backend for /connectors/{id} as well)
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Could not fetch connector');
      const result = await resp.json();
      const found = (result.connectors || []).find((c: any) => c.connector_id === connectorId);
      if (!found) throw new Error('Connector not found');
      setData(found);
      if (found.schema && found.schema.schema_id) {
        // Optionally: fetch full schema details, for now use preview
        setSchema(found.schema);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch connector');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchSchema = async () => {
    if (!tenantId || !connectorId) return;
    setFetchingSchema(true);
    setSchemaMessage(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      // For refresh: use filtered fetch (respect saved selection)
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/fetch-schema`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result?.detail || 'Failed to fetch schema');
      const pretty = { raw_schema: result.schema, fetched_at: result.fetched_at };
      setSchema(pretty);
      setData((d: any) => ({ ...(d || {}), schema: pretty, last_schema_fetch: result.fetched_at }));
      setSchemaMessage('Schema fetched successfully');
      setAutoSchemaFetch(false); // Reset auto-fetch flag
      // No need to fetchDetail() - schema and last_schema_fetch already updated in state
    } catch (err: any) {
      setSchemaMessage(err.message || 'Failed to fetch schema');
      setAutoSchemaFetch(false);
    } finally {
      setFetchingSchema(false);
    }
  };

  const handleSaveName = async () => {
    if (!tenantId || !connectorId || !connectorName.trim()) return;
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: connectorName.trim() }),
      });
      if (!resp.ok) throw new Error('Failed to save connector name');
      await fetchDetail();
      setNamingStep(false);
      // Auto-open file selection for Google Drive
      if (data?.type === 'google_drive') {
        setAutoFileSelection(true);
        setSelectModalOpen('drive');
        setFilesLoading(true);
        setFilesError(null);
        fetchDriveFiles();
      }
    } catch (err: any) {
      notify(err.message || 'Failed to save name', 'error');
    }
  };

  /**
   * Remove a single Drive file from the connector's saved selection.
   *
   * Mirrors the inline X-button behaviour from the previous card grid, but
   * lifted out of the JSX so DriveFilesView can call it cleanly. Also keeps
   * `selected_table_names` in sync — the build/sync paths key off both
   * fields and we don't want them drifting.
   */
  const handleRemoveDriveFile = React.useCallback(async (fileId: string) => {
    if (!tenantId || !connectorId) return;
    try {
      const currentFiles: any[] = data?.connector_metadata?.selected_drive_files || [];
      const updatedFiles = currentFiles.filter((file: any) => file.id !== fileId);
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connector_metadata: {
            selected_drive_files: updatedFiles,
            selected_table_names: updatedFiles.map((f: any) => f.id),
          },
        }),
      });
      if (!resp.ok) throw new Error('Failed to remove file');
      await fetchDetail();
      notify('File removed', 'success');
    } catch (err: any) {
      notify(err.message || 'Failed to remove file', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, connectorId, data?.connector_metadata?.selected_drive_files]);

  const handleSaveNameInline = async () => {
    if (!tenantId || !connectorId || !editedName.trim()) return;
    setSavingName(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: editedName.trim() }),
      });
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save connector name');
      }
      const result = await resp.json();
      setData({ ...data, ...result });
      setEditingName(false);
      notify('Connector name updated successfully', 'success');
    } catch (err: any) {
      notify(err.message || 'Failed to save name', 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handleDeleteConnector = async () => {
    if (!tenantId || !connectorId) return;
    setDeleting(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData?.error || 'Failed to delete connector');
      }
      // Navigate back to connectors list
      navigate('/connectors');
      notify('Connector deleted successfully', 'success');
    } catch (err: any) {
      notify(err.message || 'Failed to delete connector', 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Test connection — read-only health probe.
  //
  // The backend `/test` endpoint deliberately does NOT persist a new
  // status, so we don't touch `data.status` or `data.error_message` here
  // either. If the user has paused (status=inactive) or the connector is
  // already FAILED for a different reason, a fresh probe shouldn't
  // silently overwrite that. Instead we keep the result in transient
  // client state and the header surfaces it as "verified just now" /
  // "last test failed" copy.
  // ─────────────────────────────────────────────────────────────────────
  const handleTestConnection = async () => {
    if (!tenantId || !connectorId || testing) return;
    setTesting(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/test`),
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody?.detail || 'Connection test failed');
      }
      const result = await resp.json();
      setLastTestedAt(Date.now());
      setLastTestResult({
        success: !!result.success,
        errorMessage: result.error_message ?? null,
      });
      if (result.success) {
        notify('Connection successful', 'success');
      } else {
        notify(result.error_message || 'Connection test failed', 'error');
      }
    } catch (err: any) {
      const message = err?.message || 'Connection test failed';
      setLastTestedAt(Date.now());
      setLastTestResult({ success: false, errorMessage: message });
      notify(message, 'error');
    } finally {
      setTesting(false);
    }
  };

  // OAuth re-authenticate — uses the existing reauthorize-url endpoint.
  // It returns an OAuth provider URL we hand off via `window.location` so
  // the standard provider callback brings us back to /connectors/:id?reauth=success.
  const handleReauthenticate = async () => {
    if (!tenantId || !connectorId || reauthenticating) return;
    setReauthenticating(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/reauthorize-url`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody?.detail || 'Could not start re-authentication');
      }
      const { oauth_url } = await resp.json();
      if (oauth_url) {
        window.location.href = oauth_url;
      } else {
        throw new Error('No OAuth URL returned');
      }
    } catch (err: any) {
      notify(err?.message || 'Could not start re-authentication', 'error');
      setReauthenticating(false);
    }
  };

  // Fetch Google Drive files for the picker
  async function fetchDriveFiles() {
    if (!tenantId || !connectorId) return;
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/resources`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error('Could not fetch selectable items');
      const result = await resp.json();
      setDriveFiles(result || []);
      const files = data?.connector_metadata?.selected_drive_files || [];
      const ids = files.map((f: any) => f.id);
      setSelectedIds(ids);
      try {
        setSelectedDriveRows(files);
      } catch {}
    } catch (err: any) {
      setFilesError(err.message || 'Failed to fetch items');
    } finally {
      setFilesLoading(false);
    }
  }

  // Fetch database tables/views for the picker (lightweight; no full schema)
  async function fetchDatabaseTables() {
    if (!tenantId || !connectorId) return;
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/resources`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error('Could not fetch selectable items');
      const result = await resp.json();
      // The unified endpoint already returns: {id, name, mimeType, type}
      setDriveFiles(result || []);
      setSelectedIds(data?.connector_metadata?.selected_table_names || []);
      // Build a lookup of resource id/name → type (table/view) for icon/label display
      const typeMap: Record<string, string> = {};
      for (const item of (result || [])) {
        if (item.type) {
          typeMap[item.id || item.name] = item.type;
          if (item.name) typeMap[item.name] = item.type;
        }
      }
      setResourceTypeMap(prev => ({ ...prev, ...typeMap }));
    } catch (err: any) {
      setFilesError(err.message || 'Failed to fetch items');
    } finally {
      setFilesLoading(false);
    }
  }

  // Handle entering edit mode
  function handleEnterEditMode() {
    // Initialize editedConfig with current config values (only editable fields)
    const initialConfig: any = {};
    EDITABLE_FIELDS.forEach(field => {
      if (data?.config?.[field] !== undefined) {
        initialConfig[field] = data.config[field];
      }
    });
    setEditedConfig(initialConfig);
    setEditMode(true);
    setSaveError(null);
  }

  // Handle canceling edit mode
  function handleCancelEdit() {
    setEditMode(false);
    setEditedConfig({});
    setSaveError(null);
  }

  // Handle saving configuration changes
  async function handleSaveConfig() {
    if (!tenantId || !connectorId) return;
    setSaving(true);
    setSaveError(null);

    try {
      const token = window.localStorage.getItem('klaris_jwt');
      
      // Merge edited fields with existing config (preserve OAuth fields)
      const updatedConfig = { ...data.config, ...editedConfig };
      
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ config: updatedConfig }),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update connector configuration');
      }

      const result = await resp.json();
      
      // Update local data with response (includes config, status, error_message, etc.)
      setData({ ...data, ...result });
      setEditMode(false);
      setEditedConfig({});
      
      notify('Configuration updated successfully', 'success');
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save configuration');
      notify(err.message || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  }

  // Handle field change in edit mode
  function handleFieldChange(field: string, value: any) {
    setEditedConfig({ ...editedConfig, [field]: value });
  }

  // Prepare selection summaries when the Select tab is active
  useEffect(() => {
    if (tab !== 1) return;
    if (!data) return;
    if (data.category === 'files') {
      const files = data?.connector_metadata?.selected_drive_files || [];
      setSelectedDriveRows(files);
    } else if (data.category === 'sql' || data.category === 'rest') {
      const names = data?.connector_metadata?.selected_table_names || [];
      setSelectedTableRows(names);
    }
    // eslint-disable-next-line
  }, [tab, data, driveFiles.length]);

  // Get appropriate labels based on connector category
  const selectionLabels = data ? getSelectionLabels(data.category) : null;

  // Selected resource count, used by the header stats strip.
  const selectedCount = data?.category === 'files'
    ? (data?.connector_metadata?.selected_drive_files?.length || 0)
    : (data?.connector_metadata?.selected_table_names?.length || 0);

  // Schema-drift detection — surface a quiet banner when `/resources` has
  // tables that aren't in the saved selection (a new column has shipped
  // upstream, e.g.) so the operator knows to re-run Build Data Model.
  // Only meaningful for sql/rest connectors that already have a selection.
  const driftCount = (() => {
    if (!data) return 0;
    if (data.category !== 'sql' && data.category !== 'rest') return 0;
    const selected: string[] = data?.connector_metadata?.selected_table_names || [];
    if (selected.length === 0) return 0;
    if (allResources.length === 0) return 0;
    const availableNames = new Set(
      allResources.map((r: any) => r.id || r.name).filter(Boolean)
    );
    const stillThere = selected.filter((n) => availableNames.has(n));
    return Math.max(0, allResources.length - stillThere.length);
  })();

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
        <Box>
          {/* Page header — extracted into ConnectorDetailHeader */}
          <ConnectorDetailHeader
            data={data}
            editingName={editingName}
            editedName={editedName}
            savingName={savingName}
            setEditedName={setEditedName}
            onStartEditName={() => {
              setEditedName(data.name || '');
              setEditingName(true);
            }}
            onCancelEditName={() => setEditingName(false)}
            onSaveName={handleSaveNameInline}
            onDelete={() => setDeleteDialogOpen(true)}
            onTestConnection={handleTestConnection}
            onReauthenticate={handleReauthenticate}
            testing={testing}
            reauthenticating={reauthenticating}
            lastTestedAt={lastTestedAt}
            lastTestResult={lastTestResult}
            selectedCount={selectedCount}
            selectedItemLabel={selectionLabels?.itemLabelCapitalized || 'Items'}
            syncPoints={syncPoints}
          />

          {/* Data Sync section — only rendered for connectors whose data is
              replicated to the Klaris warehouse (Airtable today; HubSpot/
              Shopify/Drive next). Lives above the Build Data Model nudge
              because syncing data is the prerequisite to building the model. */}
          {data?.replicates_to_warehouse && tenantId && connectorId && (
            <DataSyncSection
              tenantId={tenantId}
              connectorId={connectorId}
              onSyncCompleted={fetchDetail}
            />
          )}

          {/* Quiet inline cue for the Build Data Model entry-point. For
              replicating connectors, BDM now runs Replicate Data → Refresh
              Schema → Profile → Ontology in one orchestrated job, so no
              separate "sync first" warning is needed. */}
          <Box
            sx={(t) => ({
              mb: 2.5,
              px: 1.75,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              borderRadius: `${t.ds.radius.sm}px`,
              border: `1px solid ${t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.sunken,
            })}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <AccountTreeRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                Schema, profiling and selection are managed via{' '}
                <Box
                  component="span"
                  onClick={() => navigate('/data-sync')}
                  sx={(t) => ({
                    color: t.tokens.brand.primary,
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  })}
                >
                  Build Data Model
                </Box>
                .
              </Typography>
            </Stack>
          </Box>

          {/* Schema-drift banner: shown when the live /resources count is
              ahead of what's currently selected. We never delete here —
              we just nudge the user back to Build Data Model. */}
          {driftCount > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/data-sync')}
                  sx={{ fontWeight: 600 }}
                >
                  Review
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                New {selectionLabels?.itemLabel || 'items'} detected upstream
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {driftCount} {selectionLabels?.itemLabel || 'item'}
                {driftCount === 1 ? '' : 's'} are available but not in your current selection. Run Build Data Model to refresh.
              </Typography>
            </Alert>
          )}

          {/* Navigation Tabs */}
          <Box sx={{ mb: 2.5 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={(t) => ({ borderBottom: `1px solid ${t.tokens.border.subtle}`, '& .MuiTab-root': { fontSize: '0.875rem', py: 1.5 } })}>
              <Tab label="Overview" />
              <Tab label={selectionLabels?.tabLabel || 'Tables'} />
              <Tab label="Profiling" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {data.type === 'hubspot' && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
                      <StorageRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                        Overview
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5, mb: 2.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>
                          Connector Type
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                          HubSpot CRM
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>
                          Authentication
                        </Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                          {data?.config?.oauth_refresh_token ? 'OAuth' : 'Legacy App Token'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Supported Resources
                      </Typography>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {HUBSPOT_SUPPORTED_RESOURCES.map((resource) => (
                          <Chip key={resource} label={resource} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Show empty state if no config sections available */}
              {data.type !== 'hubspot' && !data.config?.host && !data.config?.port && !data.config?.database && !data.config?.user && !data.config?.account && !data.config?.schema && !data.config?.warehouse && !data.config?.role && !data.config?.password && !data.config?.shop_url && !data.config?.ssh_tunnel && !data.config?.oauth_access_token && !data.config?.oauth_refresh_token && !data.config?.token_expiry && !data.config?.access_token && !data.config?.client_id && !data.config?.service_account_email && !data.config?.project && (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }}>
                  <StorageRounded sx={{ fontSize: 42, color: 'text.disabled', mb: 0.75 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontSize: '0.875rem' }}>
                    No configuration details available
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This connector type may not have traditional connection settings
                  </Typography>
                </Paper>
              )}
              
              {/* Connection Details - Unified section for all connector types except HubSpot (managed via OAuth Overview card) */}
              {data.type !== 'hubspot' && (data.config?.host || data.config?.port || data.config?.database || data.config?.user || data.config?.account || data.config?.schema || data.config?.warehouse || data.config?.role || data.config?.password || data.config?.shop_url || data.config?.oauth_access_token || data.config?.oauth_refresh_token || data.config?.token_expiry || data.config?.client_id || data.config?.service_account_email || data.config?.project) && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <StorageRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>Connection Details</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {/*
                          NOTE: There is intentionally no manual "Refresh Token"
                          button here. Access tokens are auto-refreshed server-side
                          on every API call (see get_valid_*_credentials in
                          backend/utils/oauth_utils.py). When the *refresh* token
                          itself is invalid, the header's "Re-authenticate" action
                          runs the full OAuth consent flow — that is the only
                          recovery path users actually need.
                        */}
                        {/* Show Edit button for non-OAuth or mixed connectors with editable fields */}
                        {(data.config?.host || data.config?.port || data.config?.database || data.config?.user || data.config?.password || data.config?.shop_url || data.config?.service_account_json) && !editMode && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={handleEnterEditMode}
                          >
                            Edit
                          </Button>
                        )}
                        {editMode && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveConfig}
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={handleCancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                    {/* OAuth status: one human-readable statement, no debug rows.
                        The header above already shows token expiry as a passive
                        caption and exposes the only recovery action that matters
                        (Re-authenticate), so we don't repeat that detail here. */}
                    {data.config?.oauth_access_token && (
                      <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.875rem' }}>
                        This connection is authenticated via OAuth. Access is refreshed
                        automatically - no action needed. If access is ever lost, use{' '}
                        <strong>Re-authenticate</strong> in the top-right to reconnect.
                      </Alert>
                    )}
                    {saveError && (
                      <Alert severity="error" sx={{ mb: 2, fontSize: '0.875rem' }}>
                        {saveError}
                      </Alert>
                    )}
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5 }}>
                      {data.config?.host && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Host</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.host ?? data.config.host}
                            onChange={(e) => handleFieldChange('host', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.host} />
                        )}
                      </Box>
                    )}
                    {data.config?.port && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Port</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={editedConfig.port ?? data.config.port}
                            onChange={(e) => handleFieldChange('port', parseInt(e.target.value) || e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.port} />
                        )}
                      </Box>
                    )}
                    {data.config?.database && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Database</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.database ?? data.config.database}
                            onChange={(e) => handleFieldChange('database', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.database} />
                        )}
                      </Box>
                    )}
                    {data.config?.user && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>User</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.user ?? data.config.user}
                            onChange={(e) => handleFieldChange('user', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.user} />
                        )}
                      </Box>
                    )}
                    {data.config?.account && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Account</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.account ?? data.config.account}
                            onChange={(e) => handleFieldChange('account', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.account} />
                        )}
                      </Box>
                    )}
                    {data.config?.schema && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Schema</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.schema ?? data.config.schema}
                            onChange={(e) => handleFieldChange('schema', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.schema} />
                        )}
                      </Box>
                    )}
                    {data.config?.warehouse && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Warehouse</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.warehouse ?? data.config.warehouse}
                            onChange={(e) => handleFieldChange('warehouse', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.warehouse} />
                        )}
                      </Box>
                    )}
                    {data.config?.role && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Role</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.role ?? data.config.role}
                            onChange={(e) => handleFieldChange('role', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.role} />
                        )}
                      </Box>
                    )}
                    {data.config?.project && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Project ID</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.project ?? data.config.project}
                            onChange={(e) => handleFieldChange('project', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.project} />
                        )}
                      </Box>
                    )}
                    {data.config?.service_account_email && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Service Account</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={data.config.service_account_email}
                            disabled
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.service_account_email} breakAll />
                        )}
                      </Box>
                    )}
                    {data.type === 'bigquery' && !data.config?.service_account_email && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Authentication</Typography>
                        <Chip label="Service Account" size="small" color="success" sx={{ fontWeight: 500 }} />
                      </Box>
                    )}
                    {editMode && data.type === 'bigquery' && (
                      <Box sx={{ gridColumn: 'span 2' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Replace Service Account JSON</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={4}
                          placeholder="Paste new service account JSON to replace credentials"
                          value={editedConfig.service_account_json ?? ''}
                          onChange={(e) => handleFieldChange('service_account_json', e.target.value)}
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          helperText="Leave empty to keep existing credentials"
                        />
                      </Box>
                    )}
                    {data.config?.shop_url && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Shop URL</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedConfig.shop_url ?? data.config.shop_url}
                            onChange={(e) => handleFieldChange('shop_url', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <CopyableLine value={data.config.shop_url} breakAll />
                        )}
                      </Box>
                    )}
                    {data.config?.scope && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Scope</Typography>
                        <CopyableLine value={data.config.scope} breakAll />
                      </Box>
                    )}
                    {data.config?.password && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Password</Typography>
                        {editMode ? (
                          <TextField
                            fullWidth
                            size="small"
                            type={visibleSecrets.has('password') ? 'text' : 'password'}
                            value={editedConfig.password ?? data.config.password}
                            onChange={(e) => handleFieldChange('password', e.target.value)}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            InputProps={{
                              endAdornment: (
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const newVisible = new Set(visibleSecrets);
                                    if (visibleSecrets.has('password')) {
                                      newVisible.delete('password');
                                    } else {
                                      newVisible.add('password');
                                    }
                                    setVisibleSecrets(newVisible);
                                  }}
                                  edge="end"
                                >
                                  {visibleSecrets.has('password') ? <VisibilityOffRounded sx={{ fontSize: 16 }} /> : <VisibilityRounded sx={{ fontSize: 16 }} />}
                                </IconButton>
                              )
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {visibleSecrets.has('password') ? data.config.password : '••••••'}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                const newVisible = new Set(visibleSecrets);
                                if (visibleSecrets.has('password')) {
                                  newVisible.delete('password');
                                } else {
                                  newVisible.add('password');
                                }
                                setVisibleSecrets(newVisible);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              {visibleSecrets.has('password') ? <VisibilityOffRounded sx={{ fontSize: 16 }} /> : <VisibilityRounded sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}
                    {/*
                      Removed: per-field OAuth diagnostic rows (Token Expiry,
                      Access Token, Refresh Token). Tokens are auto-refreshed
                      on every API call and the only recovery action — Re-
                      authenticate — already lives in the header. Surfacing
                      "Valid/Available" chips and a countdown to a token that
                      auto-renews invents anxiety without giving the user
                      anything to act on.
                    */}
                    {data.config?.client_id && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>Client ID</Typography>
                        <CopyableLine value={data.config.client_id} breakAll />
                      </Box>
                    )}
                    </Box>
                  </CardContent>
                </Card>
              )}
              
              
              {/* SSH Tunnel (if configured) */}
              {data.config?.ssh_tunnel && (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
                      <SettingsEthernetRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>SSH Tunnel</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5 }}>
                      {data.config.ssh_tunnel.ssh_host && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>SSH Host</Typography>
                          <CopyableLine value={data.config.ssh_tunnel.ssh_host} />
                        </Box>
                      )}
                      {data.config.ssh_tunnel.ssh_port && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>SSH Port</Typography>
                          <CopyableLine value={data.config.ssh_tunnel.ssh_port} />
                        </Box>
                      )}
                      {data.config.ssh_tunnel.ssh_user && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.375 }}>SSH User</Typography>
                          <CopyableLine value={data.config.ssh_tunnel.ssh_user} />
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

            {false && (
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                      Last fetched: {data.last_schema_fetch ? formatRelativeTime(data.last_schema_fetch) : 'Never'} · To refresh, use <Button size="small" variant="text" sx={{ p: 0, minWidth: 'unset', fontWeight: 600, fontSize: '0.8125rem', verticalAlign: 'baseline' }} onClick={() => navigate('/data-sync')}>Build Data Model →</Button>
                    </Typography>
                  </Stack>
                  
                  {/* Structured View */}
                  <Box>
                      {schemaLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : schemaError ? (
                        <Alert severity="error">{schemaError}</Alert>
                      ) : (schema?.raw_schema || data?.schema?.raw_schema) ? (
                        <Box>
                          {(() => {
                            const rawSchema = schema?.raw_schema || data?.schema?.raw_schema;
                            // Support both formats: entities (new) and tables (old)
                            const entities = rawSchema?.schema?.entities || rawSchema?.entities || rawSchema?.tables || [];
                            
                            if (!entities || entities.length === 0) {
                              return (
                                <Alert severity="info">
                                  No tables found in schema. The schema might be empty or in an unexpected format.
                                </Alert>
                              );
                            }
                            
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                  {entities.length} {entities.length === 1 ? 'Table' : 'Tables'}
                                </Typography>
                                {entities.map((entity: any, idx: number) => {
                                  const tableName = entity.name || entity.table_name || entity.id || `Table ${idx + 1}`;
                                  // Support both 'fields' and 'columns'
                                  const columns = entity.fields || entity.columns || [];
                                  const isExpanded = expandedTables.has(tableName);
                                  
                                  return (
                                    <Card key={tableName} variant="outlined" sx={{ borderRadius: 1.5 }}>
                                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box 
                                          sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                          }}
                                          onClick={() => {
                                            const newExpanded = new Set(expandedTables);
                                            if (isExpanded) {
                                              newExpanded.delete(tableName);
                                            } else {
                                              newExpanded.add(tableName);
                                            }
                                            setExpandedTables(newExpanded);
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ 
                                              width: 36, 
                                              height: 36, 
                                              borderRadius: 1.5,
                                              bgcolor: 'primary.main',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              color: 'white'
                                            }}>
                                              {entity.kind === 'file' ? (
                                                <InsertDriveFileRounded sx={{ fontSize: 18 }} />
                                              ) : (
                                                <TableChartRounded sx={{ fontSize: 18 }} />
                                              )}
                                            </Box>
                                            <Box>
                                              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.9375rem' }}>
                                                {tableName}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {columns.length} {columns.length === 1 ? 'column' : 'columns'}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          <IconButton 
                                            size="small"
                                            sx={{ 
                                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                              transition: 'transform 0.2s'
                                            }}
                                          >
                                            <ExpandMoreIcon />
                                          </IconButton>
                                        </Box>
                                        
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Divider sx={{ my: 2 }} />
                                          <Box sx={{ pl: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                                              Columns
                                            </Typography>
                                            <Stack spacing={1}>
                                              {columns.map((col: any, colIdx: number) => {
                                                const colName = col.name || col.column_name || `Column ${colIdx + 1}`;
                                                const colType = col.type || col.data_type || 'UNKNOWN';
                                                const isPrimaryKey = col.primary_key || col.is_primary_key || false;
                                                const isNullable = col.nullable !== false;
                                                
                                                return (
                                                  <Box 
                                                    key={colName}
                                                    sx={{ 
                                                      display: 'flex', 
                                                      alignItems: 'center',
                                                      gap: 1.5,
                                                      py: 0.75,
                                                      px: 1.5,
                                                      bgcolor: 'background.default',
                                                      borderRadius: 1,
                                                      border: '1px solid',
                                                      borderColor: 'divider'
                                                    }}
                                                  >
                                                    {isPrimaryKey && (
                                                      <Tooltip title="Primary Key">
                                                        <KeyRounded sx={{ fontSize: 16, color: 'warning.main' }} />
                                                      </Tooltip>
                                                    )}
                                                    {!isPrimaryKey && (
                                                      <DataObjectRounded sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5 }} />
                                                    )}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                                                        {colName}
                                                      </Typography>
                                                    </Box>
                                                    <Chip 
                                                      label={colType}
                                                      size="small"
                                                      sx={{ 
                                                        height: 20,
                                                        fontSize: '0.6875rem',
                                                        fontFamily: 'monospace',
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText'
                                                      }}
                                                    />
                                                    {!isNullable && (
                                                      <Chip 
                                                        label="NOT NULL"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ 
                                                          height: 20,
                                                          fontSize: '0.6875rem'
                                                        }}
                                                      />
                                                    )}
                                                  </Box>
                                                );
                                              })}
                                            </Stack>
                                          </Box>
                                        </Collapse>
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </Box>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                          No schema available yet. Use <Button size="small" variant="text" sx={{ p: 0, minWidth: 'unset', fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }} onClick={() => navigate('/data-sync')}>Build Data Model →</Button> to fetch schema.
                        </Typography>
                      )}
                    </Box>
                </CardContent>
              </Card>
            )}

            {tab === 1 && (
              <Card>
                <CardContent>
                  <Stack direction="column" gap={2}>
              {data.category === 'files' && (
                <DriveFilesView
                  tenantId={tenantId || ''}
                  connectorId={connectorId || ''}
                  files={(selectedDriveRows as any[]) as DriveFile[]}
                  loading={filesLoading}
                  onRemove={handleRemoveDriveFile}
                  onEdit={() => setDriveModalOpen(true)}
                />
              )}

              {/* Unified table browser - all available tables from /resources, columns from schema */}
              {(data.category === 'sql' || data.category === 'rest') && (() => {
                const selectedSet = new Set<string>(data?.connector_metadata?.selected_table_names || []);
                const selectedCount = selectedSet.size;

                // Build schema column lookup keyed by table name
                const rawSchema = schema?.raw_schema || data?.schema?.raw_schema;
                const schemaEntities: any[] = rawSchema?.schema?.entities || rawSchema?.entities || rawSchema?.tables || [];
                const schemaByName: Record<string, any> = {};
                for (const e of schemaEntities) {
                  const n = e.name || e.table_name || e.id || '';
                  if (n) schemaByName[n] = e;
                }

                // Rows from live /resources endpoint; fall back to selected names if not yet loaded
                const rows: { name: string; kind: string; selected: boolean }[] = allResources.length > 0
                  ? allResources.map((r: any) => {
                      const name = r.id || r.name || '';
                      const isView = r.type === 'view';
                      const kind = data.category === 'rest' ? 'Resource' : isView ? 'View' : 'Table';
                      return { name, kind, selected: selectedSet.has(name) };
                    })
                  : Array.from(selectedSet).map(name => ({
                      name,
                      kind: data.category === 'rest' ? 'Resource' : 'Table',
                      selected: true,
                    }));

                const itemLabel = selectionLabels?.itemLabel || 'table';
                const itemLabelPlural = `${itemLabel}s`;

                if (resourcesLoading) {
                  return (
                    <Box>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Skeleton variant="rounded" width={120} height={22} />
                        <Skeleton variant="rounded" width={200} height={20} />
                      </Stack>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                            <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width={`${40 + (i * 17) % 40}%`} height={18} />
                              <Skeleton variant="text" width={80} height={14} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                }

                return (
                  <>
                    {/* Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        {rows.length > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                            {rows.length} {rows.length !== 1 ? itemLabelPlural : itemLabel}
                          </Typography>
                        )}
                        {selectedCount > 0 && (
                          <Chip label={`${selectedCount} selected`} color="primary" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                        )}
                        {data.last_schema_fetch && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                            · Schema last fetched {formatRelativeTime(data.last_schema_fetch)}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        To change selection, use{' '}
                        <Button size="small" variant="text" sx={{ p: 0, minWidth: 'unset', fontWeight: 600, fontSize: '0.8125rem', verticalAlign: 'baseline' }} onClick={() => navigate('/data-sync')}>
                          Build Data Model →
                        </Button>
                      </Typography>
                    </Stack>

                    {rows.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {rows.map(({ name, kind, selected }) => {
                          const entity = schemaByName[name];
                          const columns: any[] = entity ? (entity.fields || entity.columns || []) : [];
                          const isExpanded = expandedTables.has(name);
                          const hasColumns = columns.length > 0;

                          return (
                            <Box
                              key={name}
                              sx={{
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: selected ? 'primary.main' : 'divider',
                                bgcolor: selected ? (t: any) => `${t.palette.primary.main}0d` : 'background.paper',
                                overflow: 'hidden',
                                transition: 'border-color 0.15s',
                              }}
                            >
                              {/* Row header */}
                              <Box
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1.5,
                                  px: 1.5, py: 1,
                                  cursor: hasColumns ? 'pointer' : 'default',
                                  '&:hover': hasColumns ? { bgcolor: 'action.hover' } : {},
                                }}
                                onClick={() => {
                                  if (!hasColumns) return;
                                  const next = new Set(expandedTables);
                                  isExpanded ? next.delete(name) : next.add(name);
                                  setExpandedTables(next);
                                }}
                              >
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: 1, flexShrink: 0,
                                  bgcolor: selected ? 'primary.main' : 'action.selected',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                                }}>
                                  {kind === 'View' ? <VisibilityRounded sx={{ fontSize: 14 }} /> : <TableChartRounded sx={{ fontSize: 14 }} />}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={selected ? 600 : 400} sx={{ fontSize: '0.8375rem' }} noWrap title={name}>
                                    {name}
                                  </Typography>
                                  {hasColumns && (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                                      {columns.length} column{columns.length !== 1 ? 's' : ''}
                                    </Typography>
                                  )}
                                </Box>
                                {selected && <CheckCircleRounded sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />}
                                {hasColumns && (
                                  <IconButton size="small" tabIndex={-1} sx={{ ml: 0.5, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                )}
                              </Box>

                              {/* Expanded columns */}
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Divider />
                                <Box sx={{ px: 2, py: 1.5 }}>
                                  <Stack spacing={0.5}>
                                    {columns.map((col: any, ci: number) => {
                                      const colName = col.name || col.column_name || `col_${ci}`;
                                      const colType = col.type || col.data_type || 'UNKNOWN';
                                      const isPK = col.primary_key || col.is_primary_key || false;
                                      const notNull = col.nullable === false;
                                      return (
                                        <Box key={colName} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, px: 1, borderRadius: 1, bgcolor: 'background.default' }}>
                                          {isPK
                                            ? <Tooltip title="Primary Key"><KeyRounded sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} /></Tooltip>
                                            : <DataObjectRounded sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                                          }
                                          <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: 500 }} noWrap>{colName}</Typography>
                                          <Chip label={colType} size="small" sx={{ height: 18, fontSize: '0.6875rem', fontFamily: 'monospace', bgcolor: 'primary.main', color: 'primary.contrastText', '& .MuiChip-label': { px: 0.75 } }} />
                                          {notNull && <Chip label="NOT NULL" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } }} />}
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }}>
                        <TableChartRounded sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>No {itemLabelPlural} found</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Run <Button size="small" variant="text" sx={{ p: 0, minWidth: 'unset', fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }} onClick={() => navigate('/data-sync')}>Build Data Model →</Button> to fetch {itemLabelPlural} and set up selection.
                        </Typography>
                      </Paper>
                    )}
                  </>
                );
              })()}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {tab === 2 && (
              <ProfilingTab connectorId={connectorId!} tenantId={tenantId!} />
            )}

      {autoSchemaFetch && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Setting up your connector... Automatically fetching schema from selected files.
          </Typography>
        </Alert>
      )}
        </Box>
      )}
      {/* Schema modal removed in favor of inline view */}
      <AppDialog
        open={!!selectModalOpen}
        onClose={() => { setSelectModalOpen(false); setPickerSearch(''); }}
        size="md"
        icon={
          <Box sx={(t) => ({
            width: 36, height: 36, borderRadius: `${t.ds.radius.md}px`,
            bgcolor: t.tokens.brand.primarySoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          })}>
            {selectModalOpen === 'drive'
              ? <InsertDriveFileRounded sx={{ fontSize: 20, color: 'primary.main' }} />
              : <StorageRounded sx={{ fontSize: 20, color: 'primary.main' }} />}
          </Box>
        }
        title={selectModalOpen === 'drive'
          ? 'Select files'
          : `Select ${selectionLabels?.itemLabelCapitalized || 'items'}`}
        subtitle={`${driveFiles.length} available · ${selectedIds.length} selected`}
        busy={savingSelection}
        contentSx={{ px: 0, py: 0 }}
        primaryAction={{
          label: savingSelection ? 'Saving…' : `Save selection${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`,
          onClick: async () => {
            setSavingSelection(true);
            try {
              const token = window.localStorage.getItem('klaris_jwt');
              const patchBody = selectModalOpen === 'drive'
                ? { connector_metadata: { selected_drive_files: driveFiles.filter((f: any) => selectedIds.includes(f.id)).map((f: any) => ({ id: f.id, name: f.name, mimeType: f.mimeType })) } }
                : { connector_metadata: { selected_table_names: selectedIds } };
              const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}`),
                {
                  method: 'PATCH',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(patchBody),
                }
              );
              if (!resp.ok) throw new Error('Failed to save selection');
              await fetchDetail();
              if (selectModalOpen === 'drive') {
                setSelectedDriveRows(driveFiles.filter((f) => selectedIds.includes(f.id)));
              } else {
                setSelectedTableRows([...(selectedIds as string[])]);
              }
              setSelectModalOpen(false);
              setPickerSearch('');

              if (autoFileSelection && selectedIds.length > 0) {
                setAutoFileSelection(false);
                setAutoSchemaFetch(true);
                setTimeout(() => {
                  handleFetchSchema();
                }, 500);
              }
            } catch (err: any) {
              setFilesError(err.message || 'Failed to save');
            }
            setSavingSelection(false);
          },
          disabled: filesLoading || selectedIds.length === 0,
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => { setSelectModalOpen(false); setPickerSearch(''); } }}
        actionsLeftExtra={
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {selectedIds.length > 0 ? `${selectedIds.length} ${selectionLabels?.itemLabel || 'items'} selected` : `No ${selectionLabels?.itemLabel || 'items'} selected`}
          </Typography>
        }
      >
        <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
          {!filesLoading && !filesError && driveFiles.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <TextField
                placeholder={`Search ${selectionLabels?.itemLabel || 'items'}...`}
                size="small"
                fullWidth
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ display: 'flex', mr: 0.75, color: 'text.disabled' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    </Box>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8125rem', height: 36 },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const allowed = (selectModalOpen === 'drive'
                    ? driveFiles.filter((f: any) => f && f.mimeType !== 'application/vnd.google-apps.folder')
                    : driveFiles
                  );
                  const allIds = allowed.map((f: any) => f.id);
                  const next = Array.from(new Set([...(selectedIds as string[]), ...allIds]));
                  setSelectedIds(next);
                }}
                sx={{ borderRadius: '7px', textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, borderColor: 'divider', color: 'text.secondary', fontWeight: 500, px: 1.5 }}
              >
                Select All
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedIds([])}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, color: 'text.secondary', fontWeight: 500, px: 1 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          )}
          <Divider />
        </Box>

        <Box sx={{ px: 3, py: 0, flex: 1, overflow: 'auto' }}>
          {filesLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>Loading available {selectionLabels?.itemLabel || 'items'}...</Typography>
            </Box>
          ) : filesError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{filesError}</Alert>
          ) : (() => {
            const isDrive = selectModalOpen === 'drive';
            const searchLower = pickerSearch.toLowerCase();
            const filteredFiles = driveFiles
              .filter((f: any) => !isDrive || (f && f.mimeType !== 'application/vnd.google-apps.folder'))
              .filter((f: any) => {
                if (!searchLower) return true;
                const label = isDrive ? f.name : (f.name || f.id);
                return label?.toLowerCase().includes(searchLower);
              });

            // Group by schema for database connectors
            const isDatabase = !isDrive;
            const groups: { [schema: string]: any[] } = {};
            if (isDatabase) {
              filteredFiles.forEach((f: any) => {
                const name = f.name || f.id;
                const dotIdx = name.indexOf('.');
                const schema = dotIdx > 0 ? name.substring(0, dotIdx) : 'default';
                if (!groups[schema]) groups[schema] = [];
                groups[schema].push(f);
              });
            }
            const schemaKeys = Object.keys(groups).sort();
            const hasMultipleSchemas = schemaKeys.length > 1;

            if (filteredFiles.length === 0) {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
                  <StorageRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary">No {selectionLabels?.itemLabel || 'items'} found</Typography>
                  {pickerSearch && (
                    <Typography variant="caption" color="text.disabled">Try a different search term</Typography>
                  )}
                </Box>
              );
            }

            const renderItem = (f: any) => {
              const isSelected = selectedIds.includes(f.id);
              const label = isDrive ? f.name : (f.name || f.id);
              const tableName = isDatabase && label.includes('.') ? label.substring(label.indexOf('.') + 1) : label;
              return (
                <Box
                  key={f.id}
                  onClick={() => {
                    setSelectedIds(isSelected
                      ? selectedIds.filter((id: string) => id !== f.id)
                      : [...selectedIds, f.id]);
                  }}
                  sx={(t) => ({
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 1, borderRadius: `${t.ds.radius.md}px`, cursor: 'pointer',
                    border: `1px solid ${isSelected ? t.tokens.brand.primary : t.tokens.border.subtle}`,
                    bgcolor: isSelected ? t.tokens.brand.primarySofter : 'transparent',
                    transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                    '&:hover': {
                      borderColor: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySofter,
                    },
                  })}
                >
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                    tabIndex={-1}
                    disableRipple
                  />
                  <Box sx={(t) => ({
                    width: 28, height: 28, borderRadius: `${t.ds.radius.sm}px`, flexShrink: 0,
                    bgcolor: isSelected ? t.tokens.brand.primarySoft : t.tokens.surface.sunken,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                  })}>
                    {isDrive
                      ? <InsertDriveFileRounded sx={{ fontSize: 15, color: isSelected ? 'primary.main' : 'text.disabled' }} />
                      : f.type === 'view'
                        ? <VisibilityRounded sx={{ fontSize: 15, color: isSelected ? 'primary.main' : 'text.disabled' }} />
                        : <TableChartRounded sx={{ fontSize: 15, color: isSelected ? 'primary.main' : 'text.disabled' }} />}
                  </Box>
                  <Typography variant="body2" sx={{
                    fontSize: '0.8125rem', fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'text.primary' : 'text.secondary',
                    fontFamily: isDrive ? 'inherit' : 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {isDrive ? label : tableName}
                  </Typography>
                </Box>
              );
            };

            if (isDatabase && hasMultipleSchemas) {
              return (
                <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {schemaKeys.map((schema) => (
                    <Box key={schema}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                        <FolderRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                          {schema}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6875rem' }}>
                          ({groups[schema].length})
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 0.75 }}>
                        {groups[schema].map(renderItem)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            }

            return (
              <Box sx={{ py: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 0.75 }}>
                {filteredFiles.map(renderItem)}
              </Box>
            );
          })()}
        </Box>
      </AppDialog>
      
      <AppDialog
        open={namingStep}
        onClose={() => setNamingStep(false)}
        size="sm"
        title="Name your Google Drive connector"
        primaryAction={{
          label: 'Continue',
          onClick: handleSaveName,
          disabled: !connectorName.trim(),
        }}
        secondaryAction={{ label: 'Skip', color: 'inherit', onClick: () => setNamingStep(false) }}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Give your Google Drive connector a name to help you identify it later.
        </Typography>
        <TextField
          autoFocus
          label="Connector name"
          placeholder="e.g., My Google Drive Documents"
          fullWidth
          value={connectorName}
          onChange={(e) => setConnectorName(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && connectorName.trim()) {
              handleSaveName();
            }
          }}
          margin="normal"
        />
      </AppDialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConnector}
        title="Delete connector"
        description={
          <>
            You're about to permanently delete{' '}
            <strong>{data?.name || `${data?.type} connector`}</strong>. This will remove
            all stored schemas, profiling data, and selection metadata for this source.
          </>
        }
        body={
          <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
            Dashboards, saved queries, and ontology nodes that reference this connector
            will lose their data source. This action cannot be undone.
          </Alert>
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete connector'}
        tone="destructive"
        busy={deleting}
        // Force the operator to type the connector name — the standard
        // "I really mean it" pattern. We fall back to the type if the
        // connector doesn't have a custom name set yet.
        requireTypeToConfirm={data?.name || data?.type || undefined}
      />

      {/* Canonical Drive files modal — same one used on /data-sync. Mounting
          it here lets users edit selection without leaving the detail page. */}
      {data?.type === 'google_drive' && tenantId && connectorId && driveModalOpen && (
        <DriveFilesPickerModal
          open={driveModalOpen}
          connectorId={connectorId}
          connectorName={data?.name}
          tenantId={tenantId}
          authHeader={() => {
            const token = window.localStorage.getItem('klaris_jwt');
            const headers: Record<string, string> = {};
            if (token) headers.Authorization = `Bearer ${token}`;
            return headers;
          }}
          initialFiles={(data?.connector_metadata?.selected_drive_files || []) as DriveFile[]}
          onClose={() => setDriveModalOpen(false)}
          onSaved={() => { fetchDetail(); }}
        />
      )}
    </Box>
  );
}
